import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { parseDepth } from "../../../utils/commonComponent/parse";
import { DisciplineOptions } from "../../disciplineMaster/disiplineConst";
import { drawTables } from "./renderTables";
import { config } from "./datasheetConfig";
import { 
  drawText, 
  getFooterHeight, 
  getHeaderHeight, 
  initDefaultPage
} from "./commonTable";


export const observationSheetConfigData = {
  observationSheetProps: {}
};

// Function to generate single datasheet content
const generateSingleDatasheet = async (doc, observationSheet, isFirstDatasheet, datasheetStartPage) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const totalPageWidth = pageWidth - config.contentMargin * 2;

  // Draw header and footer on first page of this datasheet
  initDefaultPage(doc, observationSheet);

  // Calculate starting Y position after header
  let currentY = getHeaderHeight() + config.headerSpacing;

  // --- Test Details Table ---
  const testDetails = [
    { label: "Group", value: observationSheet?.discipline_groupName },
    { label: "Material", value: observationSheet?.materialScope_MaterialName },
    { label: "BH No./Trial Pit No.", value: observationSheet?.observationSheet_boreHoleNo },
    { label: "Location/Chainage No./Source.", value: observationSheet?.inwardTests_locationChainageNo },
    { label: "From Depth", value: parseDepth(observationSheet?.inwardTests_fromDepth) },
    { label: "To Depth", value: parseDepth(observationSheet?.inwardTests_toDepth) },
    { label: "Sample Type", value: observationSheet?.inwardTests_sampleType },
    { label: "Quantity", value: observationSheet?.inwardTests_quantity },
    { label: "Length Of Rock", value: parseDepth(observationSheet?.inwardTests_lengthOfRock) },
  ];

  const rows = [];
  for (let i = 0; i < testDetails.length; i += 2) {
    const row = [];
    row.push(testDetails[i].label, testDetails[i].value || "");
    if (testDetails[i + 1])
      row.push(testDetails[i + 1].label, testDetails[i + 1].value || "");
    else row.push("", "");
    rows.push(row);
  }

  // Calculate 25% width for each of 4 columns
  const columnWidth25Percent = totalPageWidth / 4;

  autoTable(doc, {
    startY: currentY,
    body: rows,
    theme: "grid",
    styles: {
      cellPadding: { top: 3, left: 2, bottom: 3, right: 2 },
      fontSize: config.fontSize - 1,
      halign: "left",
      valign: "middle",
      textColor: 0,
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: columnWidth25Percent },
      1: { cellWidth: columnWidth25Percent },
      2: { cellWidth: columnWidth25Percent },
      3: { cellWidth: columnWidth25Percent },
    },
    margin: {
      top: getHeaderHeight() + config.headerSpacing,
      left: config.contentMargin,
      right: config.contentMargin,
      bottom: getFooterHeight() + 10,
    },
    didParseCell: function (data) {
      if (data.column.index === 0 || data.column.index === 2) {
        data.cell.styles.fontStyle = "bold";
      }
    },
    didDrawPage: (data) => {
      // Draw header and footer on every new page
      if (data.pageNumber > datasheetStartPage) {
        initDefaultPage(doc, observationSheet);
      }
    },
  });

  currentY = doc.lastAutoTable.finalY + 15;

  // --- Discipline ---
  const disciplineLabel = DisciplineOptions.find(
    (key) => key.id == observationSheet?.inward_disciplineId
  )?.label || "";
  
  drawText(doc, `DISCIPLINE: ${disciplineLabel}`, config.contentMargin, currentY, { 
    bold: true, 
    fontSize: config.fontSize - 1 
  });

  // --- Dynamic Tables ---
  if (observationSheet?.staticTables?.length > 0 && observationSheet?.readingRows?.length > 0) {
    const props = {
      datasheetDetails: observationSheet,
      margin: config.contentMargin,
      tableMargin: {
        left: config.contentMargin,
        right: config.contentMargin,
        top: getHeaderHeight() + config.headerSpacing,
        bottom: getFooterHeight() + 10,
      },
      // Pass callback to draw header/footer on new pages
      initPage: () => initDefaultPage(doc, observationSheet),
    };

    currentY = await drawTables(
      doc,
      currentY + 10,
      observationSheet.staticTables,
      observationSheet.readingRows,
      props
    );
  }

  // --- Remark section ---
  if (observationSheet?.observationSheet_configuration?.remark) {
    const noteText = observationSheet.observationSheet_configuration.remark;
    const usableWidth = pageWidth - config.contentMargin * 2;
    const remarkFontSize = config.fontSize - 2;
    const pageHeight = doc.internal.pageSize.getHeight();
    const availableSpace = pageHeight - getFooterHeight() - currentY - 40;

    // Check if we need a new page for the remark
    if (availableSpace < 50) {
      doc.addPage();
      initDefaultPage(doc, observationSheet);
      currentY = getHeaderHeight() + config.headerSpacing + 5;
    }

    const noteY = currentY + 15;
    drawText(doc, "Note:", config.contentMargin, noteY, {
      bold: true,
      fontSize: config.fontSize,
    });
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(remarkFontSize);
    const splitNote = doc.splitTextToSize(noteText, usableWidth);
    doc.text(splitNote, config.contentMargin, noteY + 15);
    currentY = noteY + 15 + (splitNote.length * remarkFontSize * 0.4);
  }

  // Return the last page number for this datasheet
  return doc.getNumberOfPages();
};

export const generateDatasheetPDF = async ({
  letterHead,
  draft,
  electronicSign,
  datasheetDetails,
  setLoading,
}) => {
  const doc = new jsPDF({
    orientation: "p",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  try {
    setLoading(true);
    // Allow React to render the loader before heavy work
    await new Promise((resolve) => setTimeout(resolve, 100));

    observationSheetConfigData.observationSheetProps = {
      datasheetDetails,
      letterHead,
      draft,
      electronicSign,
    };

    // Ensure datasheetDetails is an array
    const datasheetArray = Array.isArray(datasheetDetails) 
      ? datasheetDetails 
      : [datasheetDetails];

    // Track page ranges for each datasheet
    const datasheetPageRanges = [];

    // Process each datasheet
    for (let index = 0; index < datasheetArray.length; index++) {
      const singleDatasheet = datasheetArray[index];
      const isFirstDatasheet = index === 0;
      const datasheetStartPage = doc.getNumberOfPages();

      // Add new page for each datasheet after the first
      if (!isFirstDatasheet) {
        doc.addPage();
      }

      // Generate content for this datasheet
      await generateSingleDatasheet(
        doc, 
        singleDatasheet, 
        isFirstDatasheet, 
        datasheetStartPage
      );

      const endPage = doc.getNumberOfPages();

      // Store page range for this datasheet
      datasheetPageRanges.push({
        startPage: isFirstDatasheet ? 1 : datasheetStartPage + 1,
        endPage: endPage,
        datasheetDetails: singleDatasheet
      });
    }

    // --- Add page numbers to all pages ---
    const totalPages = doc.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();

      // Find which datasheet this page belongs to
      const currentDatasheetRange = datasheetPageRanges.find(
        range => i >= range.startPage && i <= range.endPage
      );

      if (currentDatasheetRange) {
        const { startPage, endPage } = currentDatasheetRange;
        const pageWithinDatasheet = i - startPage + 1;
        const totalPagesInDatasheet = endPage - startPage + 1;

        // Format: "Page No : 1 of 05"
        const pageText = `Page No : ${pageWithinDatasheet} of ${totalPagesInDatasheet.toString().padStart(2, '0')}`;

        // Position at bottom right
        const xPosition = pageWidth - config.margin;
        const yPosition = pageHeight - config.borderMargin + 6;

        drawText(doc, pageText, xPosition, yPosition, {
          align: "right",
          bold: true,
          fontSize: 9,
        });
      }
    }

    // --- Save PDF ---
    const firstJobNumber = datasheetArray[0]?.jobRegister_jobNumber || "";
    const fileName = datasheetArray.length > 1
      ? `Sheet${firstJobNumber}_and_${datasheetArray.length - 1}_more.pdf` 
      : `Sheet_${firstJobNumber}.pdf`;

    doc.save(fileName);
  } catch (error) {
    console.error("Error generating datasheet PDF:", error);
  } finally {
    setLoading(false);
  }
};